/**
 * Task Status Configuration Helper
 * 
 * Provides a centralized way for components to access the configured
 * task statuses from business.metadata.task_statuses.
 * 
 * Falls back to hardcoded defaults if no custom configuration is found.
 */

const DEFAULT_TASK_STATUSES = [
  { value: 'todo', label: 'To Do', color: '#6c757d', enabled: true, isDefault: true },
  { value: 'in_progress', label: 'In Progress', color: '#0d6efd', enabled: true, isDefault: true },
  { value: 'review', label: 'Review', color: '#ffc107', enabled: true, isDefault: true },
  { value: 'completed', label: 'Completed', color: '#198754', enabled: true, isDefault: true },
  { value: 'cancelled', label: 'Cancelled', color: '#dc3545', enabled: true, isDefault: true }
];

/**
 * Get all configured task statuses (enabled only by default)
 * @param {Object} store - Vuex store instance
 * @param {boolean} includeDisabled - If true, returns all statuses including disabled ones
 * @returns {Array} Array of status objects { value, label, color, enabled }
 */
export function getTaskStatuses(store, includeDisabled = false) {
  const business = store?.state?.business || store?.getters?.BUSINESS;
  const metadata = business?.metadata;
  
  if (metadata && metadata.task_statuses && metadata.task_statuses.length) {
    const statuses = metadata.task_statuses;
    return includeDisabled ? statuses : statuses.filter(s => s.enabled);
  }
  
  return includeDisabled ? DEFAULT_TASK_STATUSES : DEFAULT_TASK_STATUSES.filter(s => s.enabled);
}

/**
 * Get status color by status value
 * @param {Object} store - Vuex store instance
 * @param {string} statusValue - The status key (e.g., 'todo', 'in_progress')
 * @returns {string} Hex color string
 */
export function getTaskStatusColor(store, statusValue) {
  const statuses = getTaskStatuses(store, true);
  const status = statuses.find(s => s.value === statusValue);
  return status ? status.color : '#6c757d';
}

/**
 * Get status label by status value
 * @param {Object} store - Vuex store instance
 * @param {string} statusValue - The status key
 * @returns {string} Display label
 */
export function getTaskStatusLabel(store, statusValue) {
  const statuses = getTaskStatuses(store, true);
  const status = statuses.find(s => s.value === statusValue);
  return status ? status.label : statusValue;
}

/**
 * Get bootstrap-compatible color name for a status
 * Maps hex colors to closest bootstrap class names for badge styling
 * @param {Object} store - Vuex store instance
 * @param {string} statusValue - The status key
 * @returns {string} Bootstrap color class name
 */
export function getTaskStatusBootstrapColor(store, statusValue) {
  const color = getTaskStatusColor(store, statusValue);
  // Map hex colors to bootstrap names
  const colorMap = {
    '#6c757d': 'secondary',
    '#0d6efd': 'primary',
    '#ffc107': 'warning',
    '#198754': 'success',
    '#dc3545': 'danger',
    '#0dcaf0': 'info',
    '#212529': 'dark',
    '#f8f9fa': 'light'
  };
  return colorMap[color] || 'secondary';
}

/**
 * Get the next status in the workflow (for "move to next" button)
 * @param {Object} store - Vuex store instance
 * @param {string} currentStatus - Current status value
 * @returns {string|null} Next status value or null if at the end
 */
export function getNextTaskStatus(store, currentStatus) {
  const statuses = getTaskStatuses(store);
  const currentIndex = statuses.findIndex(s => s.value === currentStatus);
  
  if (currentIndex === -1 || currentIndex >= statuses.length - 1) {
    return null;
  }
  
  // Skip 'cancelled' in the flow — it's a terminal state
  const nextStatus = statuses[currentIndex + 1];
  if (nextStatus && nextStatus.value === 'cancelled') {
    return null;
  }
  
  return nextStatus ? nextStatus.value : null;
}

/**
 * Build a status flow map for drag-and-drop kanban columns
 * @param {Object} store - Vuex store instance
 * @returns {Array} Array of { value, color, label } for enabled statuses
 */
export function getKanbanStatuses(store) {
  return getTaskStatuses(store).map(s => ({
    value: s.value,
    color: s.color,
    label: s.label
  }));
}

export default {
  DEFAULT_TASK_STATUSES,
  getTaskStatuses,
  getTaskStatusColor,
  getTaskStatusLabel,
  getTaskStatusBootstrapColor,
  getNextTaskStatus,
  getKanbanStatuses
};
